// AI Service for auto-tagging and summarization
// Supports OpenAI, Claude, and Groq providers

export type AIProvider = 'openai' | 'claude' | 'groq';

export interface AISettings {
  provider: AIProvider | null;
  apiKey: string | null;
}

export interface AITaggingResult {
  tags: string[];
  confidence: number;
}

export interface AISummaryResult {
  summary: string;
  keyPoints?: string[];
}

// AI Provider configurations
const PROVIDER_CONFIG = {
  openai: {
    name: 'OpenAI',
    endpoint: 'https://api.openai.com/v1/chat/completions',
    model: 'gpt-4o-mini',
    authHeader: (key: string) => `Bearer ${key}`,
  },
  claude: {
    name: 'Claude',
    endpoint: 'https://api.anthropic.com/v1/messages',
    model: 'claude-3-5-haiku-20241022',
    authHeader: (key: string) => key, // Uses x-api-key header
  },
  groq: {
    name: 'Groq',
    endpoint: 'https://api.groq.com/openai/v1/chat/completions',
    model: 'llama-3.3-70b-versatile',
    authHeader: (key: string) => `Bearer ${key}`,
  },
};

// LocalStorage key for AI settings
const AI_SETTINGS_KEY = 'markly_ai_settings';

/**
 * Get AI settings from localStorage
 */
export function getAISettings(): AISettings {
  try {
    const stored = localStorage.getItem(AI_SETTINGS_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (e) {
    console.error('Failed to parse AI settings:', e);
  }
  return { provider: null, apiKey: null };
}

/**
 * Save AI settings to localStorage
 */
export function saveAISettings(settings: AISettings): void {
  localStorage.setItem(AI_SETTINGS_KEY, JSON.stringify(settings));
}

/**
 * Check if AI is configured
 */
export function isAIConfigured(): boolean {
  const settings = getAISettings();
  return settings.provider !== null && settings.apiKey !== null && settings.apiKey.length > 0;
}

/**
 * Test AI connection
 */
export async function testAIConnection(): Promise<{ success: boolean; message: string }> {
  const settings = getAISettings();

  if (!settings.provider || !settings.apiKey) {
    return { success: false, message: 'AI 설정이 구성되지 않았습니다.' };
  }

  try {
    const response = await callAI(
      settings.provider,
      settings.apiKey,
      'Say "Connection successful" in exactly those words.'
    );

    if (response.toLowerCase().includes('connection successful')) {
      return { success: true, message: '연결 성공!' };
    }
    return { success: true, message: `응답 받음: ${response.substring(0, 50)}...` };
  } catch (error: any) {
    return {
      success: false,
      message: error.message || '연결 실패. API 키를 확인해주세요.'
    };
  }
}

/**
 * Call AI provider API
 */
async function callAI(
  provider: AIProvider,
  apiKey: string,
  prompt: string,
  systemPrompt?: string
): Promise<string> {
  const config = PROVIDER_CONFIG[provider];

  if (provider === 'claude') {
    // Claude uses different API format
    const response = await fetch(config.endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': apiKey,
        'anthropic-version': '2023-06-01',
        'anthropic-dangerous-direct-browser-access': 'true',
      },
      body: JSON.stringify({
        model: config.model,
        max_tokens: 1024,
        system: systemPrompt || 'You are a helpful assistant.',
        messages: [{ role: 'user', content: prompt }],
      }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: { message: 'Unknown error' } }));
      throw new Error(error.error?.message || `API Error: ${response.status}`);
    }

    const data = await response.json();
    return data.content[0].text;
  } else {
    // OpenAI and Groq use the same format
    const response = await fetch(config.endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': config.authHeader(apiKey),
      },
      body: JSON.stringify({
        model: config.model,
        messages: [
          ...(systemPrompt ? [{ role: 'system', content: systemPrompt }] : []),
          { role: 'user', content: prompt },
        ],
        max_tokens: 1024,
        temperature: 0.3,
      }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: { message: 'Unknown error' } }));
      throw new Error(error.error?.message || `API Error: ${response.status}`);
    }

    const data = await response.json();
    return data.choices[0].message.content;
  }
}

/**
 * Generate tags using AI
 */
export async function generateAITags(
  url: string,
  title: string,
  description?: string,
  existingTags?: string[]
): Promise<AITaggingResult> {
  const settings = getAISettings();

  if (!settings.provider || !settings.apiKey) {
    throw new Error('AI가 설정되지 않았습니다. 설정 페이지에서 API 키를 입력해주세요.');
  }

  const systemPrompt = `You are a bookmark tagging assistant. Analyze the given URL, title, and description to suggest relevant tags.

Rules:
- Return 3-7 relevant tags
- Tags should be concise (1-3 words each)
- Include both Korean and English tags where appropriate
- Consider the content type, technology, topic, and source
- Avoid generic tags like "website" or "page"
- Return tags in JSON format: { "tags": ["tag1", "tag2", ...] }`;

  const prompt = `Please suggest tags for this bookmark:

URL: ${url}
Title: ${title}
${description ? `Description: ${description}` : ''}
${existingTags?.length ? `Existing tags (for reference, suggest different ones): ${existingTags.join(', ')}` : ''}

Return only valid JSON.`;

  try {
    const response = await callAI(settings.provider, settings.apiKey, prompt, systemPrompt);

    // Parse JSON response
    const jsonMatch = response.match(/\{[\s\S]*\}/);
    if (jsonMatch) {
      const parsed = JSON.parse(jsonMatch[0]);
      return {
        tags: Array.isArray(parsed.tags) ? parsed.tags : [],
        confidence: 0.8,
      };
    }

    // Fallback: try to extract tags from plain text
    const tags = response
      .split(/[,\n]/)
      .map((t: string) => t.trim().replace(/^["'\-\d.]+|["']+$/g, ''))
      .filter((t: string) => t.length > 0 && t.length < 30);

    return { tags, confidence: 0.6 };
  } catch (error: any) {
    console.error('AI tagging failed:', error);
    throw new Error(`AI 태그 생성 실패: ${error.message}`);
  }
}

/**
 * Generate summary using AI
 */
export async function generateAISummary(
  url: string,
  title: string,
  description?: string,
  content?: string
): Promise<AISummaryResult> {
  const settings = getAISettings();

  if (!settings.provider || !settings.apiKey) {
    throw new Error('AI가 설정되지 않았습니다. 설정 페이지에서 API 키를 입력해주세요.');
  }

  const systemPrompt = `You are a content summarization assistant. Create a concise Korean summary of the given content.

Rules:
- Summary should be 1-2 sentences (max 100 characters)
- Write in Korean
- Focus on the main value or purpose
- Be specific, not generic
- Return in JSON format: { "summary": "요약 내용", "keyPoints": ["포인트1", "포인트2"] }`;

  const contentPreview = content ? content.substring(0, 2000) : '';

  const prompt = `Please summarize this bookmark:

URL: ${url}
Title: ${title}
${description ? `Description: ${description}` : ''}
${contentPreview ? `Content preview: ${contentPreview}` : ''}

Return only valid JSON with "summary" and optional "keyPoints" fields.`;

  try {
    const response = await callAI(settings.provider, settings.apiKey, prompt, systemPrompt);

    // Parse JSON response
    const jsonMatch = response.match(/\{[\s\S]*\}/);
    if (jsonMatch) {
      const parsed = JSON.parse(jsonMatch[0]);
      return {
        summary: parsed.summary || '',
        keyPoints: parsed.keyPoints,
      };
    }

    // Fallback: use raw response as summary
    return { summary: response.substring(0, 100) };
  } catch (error: any) {
    console.error('AI summary failed:', error);
    throw new Error(`AI 요약 생성 실패: ${error.message}`);
  }
}

/**
 * Get provider display name
 */
export function getProviderName(provider: AIProvider): string {
  return PROVIDER_CONFIG[provider].name;
}

/**
 * Get all available providers
 */
export function getAvailableProviders(): { id: AIProvider; name: string }[] {
  return Object.entries(PROVIDER_CONFIG).map(([id, config]) => ({
    id: id as AIProvider,
    name: config.name,
  }));
}
